import React, { Component } from 'react';
import cx from 'classnames';
import queryString from 'query-string';
import { Amplify, Auth } from 'aws-amplify';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import Nav from '../../common/component/Nav/Nav';
import MainContainer from '../../common/component/MainContainer';
import FormContainer from '../../common/component/FormContainer';
import Footer from '../../common/component/Footer';
import config from '../../../../conf';
import CmsAPI from '../../../common/apis/CmsAPI';
import styles from './ResetPassword.module.scss';
import { CustomInput } from '../../../engage/components/Input/Input';
import { CustomButton } from '../../../engage/components/Button/Button';
import AuthorizationService from '../../../engage/service/AuthorizationService';
import MobileHeader from '../../../engage/components/MobileHeader';
import ProtectedPageSidebar from '../../../engage/components/Sidebar/ProtectedPageSidebar';
import MobilePasswordCheck from '../../common/component/MobilePasswordCheck/MobilePasswordCheck';
import { withTranslation } from 'react-i18next';
import UserAPI from '../../apis/UserAPI';
import { isLoggedIn } from '../../../engage/utils/auth-util';
import FirebaseFeature from '../../../engage/utils/firebase';

class ResetPasswordPage extends Component {
  state = {
    password: '',
    password2: '',
    errorMessage: '',
    isPasswordError: false,
    isInvalidCode: false,
    isSubmitSuccessful: false,
    isSubmit: false,
    code: '',
    username: '',
    showPassword: false,
    reqTenChars: false,
    reqLowercase: false,
    reqUppercase: false,
    reqSymbol: false,
    reqNumber: false,
    disableConfirm: true,
    showRedirectLink: false,
    isMobileView: false,
  };

  componentDidMount() {
    const qs = queryString.parse(this.props.location.search);
    console.log('Query String:', qs);
    const mediaMatch = window.matchMedia('(max-width: 900px)');
    this.setState({
      code: qs.code,
      username: qs.username,
      challenge: qs.challenge,
      isMobileView: mediaMatch.matches,
    });

    const searchWithPlusSign = this.props.location.search.replaceAll('+', '%2B');
    const { desktopEmail, challenge } = queryString.parse(searchWithPlusSign);

    if (desktopEmail && challenge) {
      this.setState({ challenge, username: desktopEmail });
    }
  }

  handleChange = e => {
    const name = e.target.name;
    const value = e.target.value;

    if (name === 'password') {
      // At least 10 characters
      if (value.length >= config.general.minPasswordLength) {
        this.setState({ reqTenChars: true });
      } else {
        this.setState({ reqTenChars: false });
      }

      // At least one lowercase character
      if (/(?=.*[a-z])/.test(value)) {
        this.setState({ reqLowercase: true });
      } else {
        this.setState({ reqLowercase: false });
      }

      // At least one uppercase character
      if (/(?=.*[A-Z])/.test(value)) {
        this.setState({ reqUppercase: true });
      } else {
        this.setState({ reqUppercase: false });
      }

      // At least one symbol
      if (/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value)) {
        this.setState({ reqSymbol: true });
      } else {
        this.setState({ reqSymbol: false });
      }

      // At least one number
      if (/\d/.test(value)) {
        this.setState({ reqNumber: true });
      } else {
        this.setState({ reqNumber: false });
      }
    }

    this.setState({ [name]: value, isPasswordError: false, isInvalidCode: false, showRedirectLink: false });
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { password, password2, reqLowercase, reqUppercase, reqSymbol, reqNumber, reqTenChars } = this.state;
    if (
      password !== prevState.password ||
      password2 !== prevState.password2 ||
      reqTenChars !== prevState.reqTenChars ||
      reqLowercase !== prevState.reqLowercase ||
      reqUppercase !== prevState.reqUppercase ||
      reqSymbol !== prevState.reqSymbol ||
      reqNumber !== prevState.reqNumber
    ) {
      this.setState({
        disableConfirm: !(password === password2 && reqTenChars && reqLowercase && reqUppercase && reqSymbol && reqNumber),
      });
    }
  }

  submitResetPassword = async () => {
    const { username, code, password, password2, challenge } = this.state;

    if (password.length < config.general.minPasswordLength) {
      this.setState({
        isPasswordError: true,
        isSubmit: false,
        errorMessage: `${this.props.t('general.password.requirement_one')} ${config.general.minPasswordLength}${' '}
        ${this.props.t('general.password.requirement_one_characters')}`,
      });
      return;
    }

    if (!this.state.reqLowercase || !this.state.reqUppercase || !this.state.reqSymbol || !this.state.reqNumber) {
      if (!this.state.reqLowercase || !this.state.reqUppercase) {
        this.setState({
          isPasswordError: true,
          isSubmit: false,
          errorMessage: this.props.t('general.password.requirement_two'),
        });
      }
      if (!this.state.reqSymbol) {
        this.setState({
          isPasswordError: true,
          isSubmit: false,
          errorMessage: this.props.t('general.password.requirement_three'),
        });
      }

      if (!this.state.reqNumber) {
        this.setState({
          isPasswordError: true,
          isSubmit: false,
          errorMessage: this.props.t('general.password.requirement_four'),
        });
      }
      return;
    }

    if (password !== password2) {
      this.setState({
        isPasswordError: true,
        isSubmit: false,
        errorMessage: 'Your passwords do not match, please try again',
      });
      return;
    }

    this.logoutIfNeeded(username);

    if (challenge === 'new_password_required') {
      const parsedUser = JSON.parse(this.props.location.state).user;
      const parsedResponse = JSON.parse(this.props.location.state).response;
      const parsedHasMFA = JSON.parse(this.props.location.state).hasMFA;

      // We can't use the parsed state directly, as it needs to be a cognitoUserObject, so we have to do some manual parsing
      // See: https://github.com/aws-amplify/amplify-js/issues/1715#issuecomment-800999983
      const pool = new CognitoUserPool({
        UserPoolId: parsedUser.pool.userPoolId,
        ClientId: parsedUser.pool.clientId,
        endpoint: parsedUser.client.endpoint,
        Storage: window.localStorage,
        AdvancedSecurityDataCollectionFlag: parsedUser.advancedSecurityDataCollectionFlag,
      });
      const cognitoUser = new CognitoUser({
        Username: parsedUser.username,
        Pool: pool,
        Storage: window.localStorage,
      });

      cognitoUser.Session = parsedUser.Session;
      try {
        // NEW_PASSWORD_REQUIRED flow
        const user = await Auth.completeNewPassword(
          cognitoUser, // the Cognito User Object
          password, // the new password
          {}
        );

        //await Auth.signOut(); // Remove saved credentials from session >> causes notauthorizedexception: access token has been revoked

        this.setState({ isSubmitSuccessful: true });

        const getPreferdMFA = await AuthorizationService.getPrefredMFA(user);

        if (parsedHasMFA === true && getPreferdMFA === 'NOMFA') {
          this.props.history.push({
            pathname: '/OTP/setup',
            state: JSON.stringify({ user: parsedUser, response: parsedResponse }),
          });
          return;
        } else if (parsedHasMFA === true && getPreferdMFA === 'SOFTWARE_TOKEN_MFA') {
          this.props.history.push({
            pathname: '/OTP/verify',
            state: JSON.stringify({ user: parsedUser, username: parsedUser.challengeParam.userAttributes.email }),
          });
          return;
        } else {
          localStorage.setItem(
            'userDetails',
            JSON.stringify({
              username: parsedUser.username,
              userPoolId: parsedUser.pool.userPoolId,
              appClientId: parsedUser.pool.clientId,
              identityPoolId: parsedResponse.IdentityPoolId,
              userPoolWebClientId: parsedResponse.UserPoolClientAppId,
              email:
                parsedUser.signInUserSession == null ? parsedUser.challengeParam.userAttributes.email : parsedUser.attributes.email,
              name: parsedUser.signInUserSession == null ? parsedUser.challengeParam.userAttributes.name : parsedUser.attributes.name,
              companyId: parsedResponse.companyId,
              companyName: parsedResponse.companyName,
              region: parsedResponse.Region,
              bucket: parsedResponse.InputBucketName,
              ssoDomain: parsedResponse.SSODomain,
            })
          );
        }

        this.props.history.push('/login', { passwordResetSucceed: true }); // Redirect to login page
      } catch (e) {
        console.log('Unable to reset password error:', e);
        let errorMessage = 'Unable to reset password';

        if (e.name === 'InvalidPasswordException') {
          errorMessage = this.getInvalidPasswordError();
        }

        this.setState({
          isPasswordError: true,
          isSubmit: false,
          errorMessage: errorMessage
        });
      }
    } else if (challenge === 'new_password_required_from_desktop_app' && username) {
      try {
        // We cannot use the search query because the temporary password can contain non encoded values such as &,=,? etc
        const fullUrl = window.location.href;
        const search = fullUrl.substring(fullUrl.indexOf('?') + 1);

        let temporaryPassword = search.split('temporaryPassword=')[1];

        let user, response;

        try {
          ({ user, response } = await AuthorizationService.login(username, temporaryPassword));
        } catch (e) {
          ({ user, response } = await AuthorizationService.login(username, decodeURIComponent(temporaryPassword)));
        }

        if (!user) {
          throw new Error('Unable to login user');
        }

        const appConfig = await CmsAPI.getAppConfig(user.username);
        const appConfigFeatures = appConfig.result.features;

        const featureMFA = appConfigFeatures.find(feature => feature.name === 'mfa_software_token');
        const featureMFAIsEnabled = featureMFA.enabled;

        const featureFirebase = appConfigFeatures.find(feature => feature.name === 'firebase');
        const featureFirebaseIsEnabled = featureFirebase.enabled;
        if (featureFirebaseIsEnabled) FirebaseFeature.initializeFirebase();

        // We can't use the parsed state directly, as it needs to be a cognitoUserObject, so we have to do some manual parsing
        // See: https://github.com/aws-amplify/amplify-js/issues/1715#issuecomment-800999983
        const pool = new CognitoUserPool({
          UserPoolId: user.pool.userPoolId,
          ClientId: user.pool.clientId,
          endpoint: user.client.endpoint,
          Storage: window.localStorage,
          AdvancedSecurityDataCollectionFlag: user.advancedSecurityDataCollectionFlag,
        });
        const cognitoUser = new CognitoUser({
          Username: user.username,
          Pool: pool,
          Storage: window.localStorage,
        });

        cognitoUser.Session = user.Session;

        // NEW_PASSWORD_REQUIRED flow
        const resetUser = await Auth.completeNewPassword(
          cognitoUser, // the Cognito User Object
          password, // the new password
          {}
        );

        //await Auth.signOut(); // Remove saved credentials from session >> causes notauthorizedexception: access token has been revoked

        this.setState({ isSubmitSuccessful: true });

        const getPreferredMFA = await AuthorizationService.getPrefredMFA(resetUser);

        if (featureMFAIsEnabled === true && getPreferredMFA === 'NOMFA') {
          this.props.history.push({
            pathname: '/OTP/setup',
            state: JSON.stringify({ user: resetUser, response: response }),
          });
          return;
        } else if (featureMFAIsEnabled === true && getPreferredMFA === 'SOFTWARE_TOKEN_MFA') {
          this.props.history.push({
            pathname: '/OTP/verify',
            state: JSON.stringify({ user: resetUser, username: resetUser.challengeParam?.userAttributes?.email }),
          });
          return;
        } else {
          localStorage.setItem(
            'userDetails',
            JSON.stringify({
              username: resetUser.username,
              userPoolId: resetUser.pool.userPoolId,
              appClientId: resetUser.pool.clientId,
              identityPoolId: response.IdentityPoolId,
              userPoolWebClientId: response.UserPoolClientAppId,
              email:
                resetUser.signInUserSession == null ? resetUser.challengeParam?.userAttributes?.email : resetUser.attributes?.email,
              name: resetUser.signInUserSession == null ? resetUser.challengeParam?.userAttributes?.name : resetUser.attributes?.name,
              companyId: response.companyId,
              companyName: response.companyName,
              region: response.Region,
              bucket: response.InputBucketName,
              ssoDomain: response.SSODomain,
            })
          );
        }

        this.props.history.push('/login', { passwordResetSucceed: true }); // Redirect to login page
      } catch (e) {
        let errorMessage = 'Unable to reset password. The reset link is invalid. Please copy and paste the URL from our email into your browser to try again';

        if (e.name === 'InvalidPasswordException') {
          errorMessage = this.getInvalidPasswordError();
        }

        console.log('Unable to reset password error:', e);
        this.setState({
          isPasswordError: true,
          isSubmit: false,
          errorMessage: errorMessage
        });
      }
    } else {
      // Forgot password flow
      try {
        const response = await CmsAPI.updatePassword(username, code, password);
        this.setState({ isSubmitSuccessful: true });
        this.props.history.push('/login', { passwordResetSucceed: true }); // Redirect to login page
      } catch (e) {
        console.log('Unable to reset password, code might be invalid error:', e);
        let errorMessage;

        if (e.name === 'InvalidPasswordException') {
          errorMessage = (
            <div>
              <p>{this.props.t('reset_password.password.weak_or_compromised')}</p>
              <ul className={styles.passwordGuidelines}>
                <li>{this.props.t('reset_password.password.reuse_not_allowed')}</li>
                <li>{this.props.t('reset_password.password.avoid_common_patterns')}</li>
                <li>{this.props.t('reset_password.password.use_passphrase')}</li>
              </ul>
            </div>
          );
        } else {
          const element = <a href="/forgot-password">Forgot Password</a>;
          errorMessage = <span>The link you used is expired or invalid, please try again by going to {element}</span>;
        }

        this.setState({
          isPasswordError: true,
          showRedirectLink: !e.name === 'InvalidPasswordException',
          isSubmit: false,
          errorMessage: errorMessage
        });
      }
    }
  };

  onClick = () => {
    this.setState({ isSubmit: true }, () => {
      setTimeout(() => {
        this.submitResetPassword();
      }, 500);
    });
  };

  onSubmitForm = () => {
    if (this.state.password && this.state.password2 && !this.state.isPasswordError) {
      this.onClick();
    }
  };

  getInvalidPasswordError = () => (
    <div>
      <p>{this.props.t('reset_password.password.weak_or_compromised')}</p>
      <ul className={styles.passwordGuidelines}>
        <li>{this.props.t('reset_password.password.reuse_not_allowed')}</li>
        <li>{this.props.t('reset_password.password.avoid_common_patterns')}</li>
        <li>{this.props.t('reset_password.password.use_passphrase')}</li>
      </ul>
    </div>
  );

  async logoutIfNeeded(username) {
    const isUserLoggedIn = await isLoggedIn();

    if (isUserLoggedIn) {
      await AuthorizationService.logOut(username);
    }
  }

  render() {
    const { reqTenChars, reqLowercase, reqUppercase, reqSymbol, reqNumber, showPassword, isMobileView } = this.state;

    return (
      <>
        {!isMobileView && <Nav />}
        <MobileHeader libraryMode={false} />
        <MainContainer className={cx('content-padding play-bg', { [styles.clearBackground]: isMobileView })}>
          {isMobileView && <ProtectedPageSidebar />}
          <div>
            <div>
              <FormContainer title={this.props.t('reset_password.new_password.title')}>
                <div className={styles.formWrapper}>
                  <form className={styles.authCard} onSubmit={this.onClick}>
                    <div className={styles.passwordsContainer}>
                      <CustomInput
                        isShow
                        id="password"
                        type="password"
                        name="password"
                        value={this.state.password}
                        fieldName={this.props.t('general.password.title')}
                        placeholder={this.props.t('general.password.your_password')}
                        invalid={this.state.isPasswordError}
                        onChange={this.handleChange}
                      />

                      <CustomInput
                        isShow
                        id="password2"
                        type="password"
                        name="password2"
                        value={this.state.password2}
                        fieldName={this.props.t('general.password.confirm_password')}
                        placeholder={this.props.t('general.password.your_password')}
                        invalid={this.state.isPasswordError}
                        validateMessage={this.state.errorMessage}
                        onChange={this.handleChange}
                      />
                    </div>

                    {isMobileView ? (
                      <MobilePasswordCheck
                        isUpperCase={reqUppercase}
                        isLowerCase={reqLowercase}
                        isOneNumber={reqNumber}
                        isSymbol={reqSymbol}
                        isMinLength={reqTenChars}
                      />
                    ) : (
                      !(reqTenChars && reqLowercase && reqUppercase && reqSymbol && reqNumber) && (
                        <div className={styles.requirementsList}>
                          <ul>
                            {!reqTenChars && (
                              <li>
                                {this.props.t('general.password.requirement_one')} {config.general.minPasswordLength}{' '}
                                {this.props.t('general.password.requirement_one_characters')}
                              </li>
                            )}
                            {(!reqLowercase || !reqUppercase) && <li>{this.props.t('general.password.requirement_two')}</li>}
                            {!reqSymbol && <li>{this.props.t('general.password.requirement_three')}</li>}
                            {!reqNumber && <li>{this.props.t('general.password.requirement_four')}</li>}
                          </ul>
                        </div>
                      )
                    )}

                    <CustomButton
                      className={cx(
                        {
                          submit: !(
                            !this.state.password ||
                            !this.state.password2 ||
                            this.state.isPasswordError ||
                            this.state.isSubmit
                          ),
                          'no-submit':
                            !this.state.password || !this.state.password2 || this.state.isPasswordError || this.state.isSubmit,
                        },
                        styles.submitButton
                      )}
                      variant="primary"
                      onClick={this.onClick}
                      loading={this.state.isSubmit}
                      loadingText={this.props.t('general.password.resetting_password')}
                      disabled={
                        !this.state.password ||
                        !this.state.password2 ||
                        this.state.isPasswordError ||
                        this.state.isSubmit ||
                        this.state.disableConfirm
                      }
                    >
                      {this.props.t('general.continue')}
                    </CustomButton>
                  </form>
                </div>
              </FormContainer>
            </div>
          </div>
        </MainContainer>
        <Footer />
      </>
    );
  }
}

export default withTranslation()(ResetPasswordPage);
